import Rollbar from 'rollbar';

let rollbarInstance = null;

/**
 * Initialize Rollbar with consent-aware configuration.
 *
 * @param {boolean} hasConsent - Whether user has granted consent for Rollbar tracking
 * @param {Object} config - Configuration object
 * @param {string} config.accessToken - Rollbar access token
 * @param {string} config.environment - Environment name (e.g., 'production', 'development')
 * @param {string} [config.instance='mkk-website-frontend'] - Instance identifier
 * @param {boolean} [forceReinit=false] - Force re-initialization even if instance exists
 * @returns {Rollbar|null} Rollbar instance or null if missing token
 */
export function initRollbar(hasConsent, { accessToken, environment, instance = 'mkk-website-frontend' } = {}, forceReinit = false) {
    if (!accessToken) {
        return null;
    }

    // Enable debug mode via localStorage or URL parameter
    const debugMode = localStorage.getItem('rollbar_debug') === 'true' ||
        window.location.search.includes('rollbar_debug=true');

    const debug = debugMode ? console.log.bind(console) : () => {};

    // Force new instance if requested
    if (forceReinit && rollbarInstance) {
        debug('[RollbarInit] Force re-initializing, creating new instance');
        rollbarInstance = null;
    }

    // Return existing instance if not forcing reinit
    if (rollbarInstance && !forceReinit) {
        return rollbarInstance;
    }

    const baseConfig = {
        accessToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
        reportLevel: 'error',
        payload: {
            environment,
            client: {
                javascript: {
                    source_map_enabled: true,
                    code_version: instance,
                },
            },
            instance,
        },
    };

    const consentConfig = hasConsent ? {
        captureIp: true,
        captureDeviceInfo: true,
        captureTelemetry: true,
        autoInstrument: {
            log: true,
            network: true,
            dom: true,
            navigation: true,
            connectivity: true,
            contentSecurityPolicy: true,
            errorOnContentSecurityPolicy: true,
        },
    } : {
        captureIp: 'anonymize',
        captureTelemetry: false,
        autoInstrument: false,
        captureDeviceInfo: false,
        scrubPaths: [ 'request.query_string' ],
        scrubFields: [ 'query_string' ],
        // Transform to remove IP addresses completely when no consent
        transform: function (payload) {
            // Remove IP from request
            if (payload.request && payload.request.user_ip) {
                delete payload.request.user_ip;
            }
            // Remove IP from client
            if (payload.client && payload.client.user_ip) {
                delete payload.client.user_ip;
            }
        },
    };

    rollbarInstance = new Rollbar({
        ...baseConfig,
        ...consentConfig,
    });

    // Expose to window for debugging and access
    window.rollbar = rollbarInstance;

    return rollbarInstance;
}
